import React from "react";
import { useNavigate } from "react-router-dom";

import { useInfiniteQuery } from "@tanstack/react-query";
import moment from "moment";

import Skeleton from "@/components/common/Skeleton";
// import LazyImage from "@/components/common/LazyImage";

import { getUserComments } from "@/services/UserService";
import useLocalStorage from "@/hooks/useLocalStorage";
import { THIRTY_MINUTES } from "@/constants/time";

const ITEMS_PER_PAGE = 10;
const CACHE_TIME = THIRTY_MINUTES; // 30 minutes

interface Property {
    id: string;
    fullAddress: string;
    imageUrls: string[];
    propertyLinkCode: string;
}

interface User {
    id: string;
    nickname: string;
}

interface Comment {
    id: string;
    content: string;
    createdAt: string;
    property: Property;
    user: User;
}

interface CommentsPage {
    data: Comment[];
    nextPage?: number;
}

interface UserCommentsProps {
    userId: string;
    isOwnProfile: boolean;
}

const CommentSkeleton = () => (
    <div className="tw-bg-white tw-p-4 !tw-mt-0 tw-border-b tw-border-[#D9D9D9]">
        <div className="tw-flex tw-items-start tw-gap-3">
            <Skeleton className="tw-w-[53px] tw-h-[53px] tw-rounded-full" />
            <div className="tw-flex-1">
                <Skeleton className="tw-w-3/4 tw-h-4 tw-mb-2 tw-rounded" />
                <Skeleton className="tw-w-1/2 tw-h-4 tw-rounded" />
            </div>
        </div>
    </div>
);

const LoadingMoreSpinner = () => (
    <div className="tw-flex tw-justify-center tw-items-center tw-py-6">
        <div className="tw-relative tw-w-8 tw-h-8">
            <div className="tw-absolute tw-inset-0 tw-border-4 tw-border-[#6C26C4] tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
            <div className="tw-absolute tw-inset-0 tw-border-4 tw-border-[#6C26C4]/20 tw-rounded-full"></div>
        </div>
    </div>
);

/**
 * Formats a full address by replacing spaces with dashes
 * @param {string} address - The full address to format
 * @returns {string} The formatted address with dashes instead of spaces
 */
const formatAddress = (address: string): string => {
    if (!address) return "";
    return address.replace(/\s+/g, "-");
};

/**
 * UserComments component that displays a user's comments with a Load More button
 * @param {Object} props - Component props
 * @param {string} props.userId - The ID of the user whose comments are being displayed
 * @param {boolean} props.isOwnProfile - Whether the comments are being displayed on the user's own profile
 * @returns {JSX.Element} UserComments component
 */
const UserComments: React.FC<UserCommentsProps> = ({ userId, isOwnProfile }) => {
    const navigate = useNavigate();
    const [socialState, setSocialState] = useLocalStorage("social-state", {});

    const {
        data: commentsData,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = useInfiniteQuery<CommentsPage>({
        // Since we already have the same key for `useQuery` in other places for ["userComments", userId],
        // we will need to use a different key here so I added "infinite" prefix
        // See https://tkdodo.eu/blog/effective-react-query-keys#caching-data:~:text=Please%20also%20be%20aware%20that%20you%20cannot%20use%20the%20same%20key%20for%20useQuery%20and%20useInfiniteQuery
        queryKey: ["infinite", "userComments", userId],
        queryFn: ({ pageParam = 1 }) => getUserComments(userId, pageParam as number, ITEMS_PER_PAGE),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            if (!lastPage.data || lastPage.data?.length < ITEMS_PER_PAGE) {
                return undefined;
            }
            return lastPage.nextPage;
        },
        enabled: !!userId,
        staleTime: 0,
        gcTime: CACHE_TIME, // Keep data in cache for 30 minutes
    });

    const handleCommentClick = (fullAddress: string, property: Property, commentId: string) => {
        if (!fullAddress) return;
        const formattedAddress = formatAddress(fullAddress);
        setSocialState({
            ...(socialState as Record<string, any>),
            property,
            commentId,
        });
        navigate(`/social/listing/${formattedAddress}/${property.propertyLinkCode}`);
    };

    if (isLoading) {
        return (
            <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
                {[...Array(3)].map((_, index) => (
                    <CommentSkeleton key={index} />
                ))}
            </div>
        );
    }

    const allComments = Array.isArray(commentsData?.pages)
        ? commentsData.pages.flatMap((page) => (page as CommentsPage).data)
        : [];

    return (
        <div className="tw-space-y-4">
            {allComments.map((comment) => (
                <div
                    key={comment.id}
                    className="tw-bg-white tw-p-4 !tw-mt-0 tw-border-b tw-border-[#D9D9D9] tw-cursor-pointer hover:tw-bg-gray-50"
                    onClick={() => handleCommentClick(comment.property.fullAddress, comment.property, comment.id)}
                >
                    <div className="tw-flex tw-items-start tw-gap-3">
                        {/* <LazyImage
                            src={comment.Property.image_urls[0]}
                            alt="Property"
                            className="tw-w-[53px] tw-h-[53px] tw-rounded-full tw-object-cover"
                        /> */}
                        <div className="tw-flex-1">
                            <div className="tw-text-[14px] tw-line-clamp-2">
                                <span className="tw-text-[#666666]">
                                    {isOwnProfile ? "You" : comment.user.nickname} commented,{" "}
                                </span>
                                <span className="tw-font-bold tw-font-merriweather">"{comment.content}"</span>
                            </div>
                            <p className="tw-m-0 tw-text-[14px] tw-text-[#666666] tw-mt-1">
                                {moment(comment.createdAt).fromNow()}
                            </p>
                        </div>
                    </div>
                </div>
            ))}

            {isFetchingNextPage && <LoadingMoreSpinner />}

            {hasNextPage && !isFetchingNextPage && (
                <div className="tw-flex tw-justify-center tw-py-2 !tw-m-0">
                    <button
                        onClick={() => fetchNextPage()}
                        className="tw-bg-white tw-px-4 tw-py-2 tw-rounded-full tw-text-[13px] tw-text-[#6C26C4] tw-font-medium hover:tw-text-[#5a1fa3] hover:tw-bg-gray-50 tw-transition-colors tw-border tw-border-[#D9D9D9]"
                    >
                        Load More Comments
                    </button>
                </div>
            )}

            {(!allComments || allComments.length === 0) && (
                <div className="tw-text-center tw-text-gray-500 tw-py-8">No comments yet</div>
            )}
        </div>
    );
};

export default UserComments;
